export function formatRupiah(amount: number): string {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function cleanWhatsAppNumber(phone: string): string {
  let cleaned = phone.replace(/[^0-9]/g, '');
  if (cleaned.startsWith('0')) {
    cleaned = '62' + cleaned.slice(1);
  }
  return cleaned;
}

export function createWhatsAppLink(phone: string, text: string): string {
  const cleanPhone = cleanWhatsAppNumber(phone);
  const encodedText = encodeURIComponent(text);
  return `https://wa.me/${cleanPhone}?text=${encodedText}`;
}

/**
 * Format date in Indonesian locale with Asia/Jakarta (UTC+7 / WIB) timezone
 * Example: "24 September 2026"
 */
export function formatOrderDate(dateInput?: string | number | Date | null): string {
  if (!dateInput) return '-';
  try {
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return '-';
    return new Intl.DateTimeFormat('id-ID', {
      timeZone: 'Asia/Jakarta',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(d);
  } catch {
    return '-';
  }
}

/**
 * Format time in Indonesian locale with Asia/Jakarta (UTC+7 / WIB) timezone
 * Example: "21:45 WIB"
 */
export function formatOrderTime(dateInput?: string | number | Date | null): string {
  if (!dateInput) return '-';
  try {
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return '-';
    const timeStr = new Intl.DateTimeFormat('id-ID', {
      timeZone: 'Asia/Jakarta',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .format(d)
      .replace('.', ':');
    return `${timeStr} WIB`;
  } catch {
    return '-';
  }
}

/**
 * Format full datetime in Indonesian locale with Asia/Jakarta (UTC+7 / WIB) timezone
 * Example: "24 September 2026 • 21:45 WIB"
 */
export function formatOrderDateTime(dateInput?: string | number | Date | null): string {
  if (!dateInput) return '-';
  const dateStr = formatOrderDate(dateInput);
  const timeStr = formatOrderTime(dateInput);
  if (dateStr === '-' || timeStr === '-') return '-';
  return `${dateStr} • ${timeStr}`;
}

/**
 * Returns canonical YYYY-MM-DD date string in Asia/Jakarta (WIB / UTC+7) timezone.
 * Prevents UTC midnight crossover bugs for Indonesian operational hours.
 */
export function getJakartaDateKey(dateInput?: string | number | Date | null): string {
  try {
    const d = dateInput !== undefined && dateInput !== null ? new Date(dateInput) : new Date();
    if (isNaN(d.getTime())) return '';
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Jakarta',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(d);
    const year = parts.find((p) => p.type === 'year')?.value || '';
    const month = parts.find((p) => p.type === 'month')?.value || '';
    const day = parts.find((p) => p.type === 'day')?.value || '';
    if (!year || !month || !day) return '';
    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
}

/**
 * Shifts a YYYY-MM-DD date key by deltaDays in Asia/Jakarta timezone.
 */
export function shiftJakartaDateKey(dateKey: string, deltaDays: number): string {
  try {
    const [y, m, d] = dateKey.split('-').map(Number);
    if (!y || !m || !d) return dateKey;
    const utcNoon = new Date(Date.UTC(y, m - 1, d + deltaDays, 12, 0, 0));
    return getJakartaDateKey(utcNoon);
  } catch {
    return dateKey;
  }
}

/**
 * Formats a YYYY-MM-DD date key into readable Indonesian date string (e.g. "Minggu, 4 Oktober 2026").
 */
export function formatJakartaDateKeyId(dateKey: string, includeWeekday = true): string {
  if (!dateKey) return '-';
  try {
    const [y, m, d] = dateKey.split('-').map(Number);
    if (!y || !m || !d) return dateKey;
    const utcNoon = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    return new Intl.DateTimeFormat('id-ID', {
      timeZone: 'Asia/Jakarta',
      ...(includeWeekday ? { weekday: 'long' } : {}),
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(utcNoon);
  } catch {
    return dateKey;
  }
}


